/** Optional Ship From province used to resolve the IANA timezone for local clocks. */
export interface ShipFromLocalClockOptions {
  provinceCode?: string | null;
}

/** Default IANA zone when province is missing or unrecognized (Eastern Canada). */
const DEFAULT_SHIP_FROM_TIME_ZONE = 'America/Toronto';

/**
 * Maps a Canadian province/territory code to the IANA timezone for Ship From local time.
 * Same-day From defaults and cancel windows must use Ship From location time, not the browser clock.
 */
export function resolveCanadianProvinceTimeZone(provinceCode: string | null | undefined): string {
  const code = (provinceCode ?? '').trim().toUpperCase();
  switch (code) {
    case 'BC':
      return 'America/Vancouver';
    case 'AB':
      return 'America/Edmonton';
    case 'SK':
      return 'America/Regina';
    case 'MB':
      return 'America/Winnipeg';
    case 'ON':
    case 'QC':
      return 'America/Toronto';
    case 'NB':
    case 'NS':
    case 'PE':
      return 'America/Halifax';
    case 'NL':
      return 'America/St_Johns';
    case 'YT':
      return 'America/Whitehorse';
    case 'NT':
      return 'America/Yellowknife';
    case 'NU':
      return 'America/Iqaluit';
    default:
      return DEFAULT_SHIP_FROM_TIME_ZONE;
  }
}

/**
 * Formats an instant as `yyyy-MM-dd'T'HH:mm:ss` in the Ship From province timezone
 * (browser local when province is omitted) for `POST /pickup/validate`.
 */
export function formatCurrentLocalTime(
  date: Date = new Date(),
  options?: ShipFromLocalClockOptions,
): string {
  const parts = resolveCivilClockParts(date, options?.provinceCode);
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

/**
 * Formats an instant as HHmm in the Ship From province timezone for same-day From defaults.
 */
export function formatCurrentLocalHhmm(
  date: Date = new Date(),
  options?: ShipFromLocalClockOptions,
): string {
  const parts = resolveCivilClockParts(date, options?.provinceCode);
  return `${parts.hour}${parts.minute}`;
}

/**
 * Returns today's date as `yyyy-MM-dd` in the Ship From province timezone
 * (browser local when province is omitted).
 */
export function formatLocalIsoDate(
  date: Date = new Date(),
  options?: ShipFromLocalClockOptions,
): string {
  const parts = resolveCivilClockParts(date, options?.provinceCode);
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * Returns the UTC epoch ms for 23:59:59.999 on a civil calendar day in the Ship From timezone.
 * Used to schedule cancel-button hide without interpreting the civil deadline in the browser TZ (OM-891).
 *
 * @param calendarDate Civil day as `yyyy-MM-dd`
 * @param options Ship From province (null/unknown → America/Toronto)
 */
export function resolveEndOfLocalDayUtcMs(
  calendarDate: string,
  options?: ShipFromLocalClockOptions,
): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(calendarDate.trim());
  if (!match) {
    return Number.NaN;
  }

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timeZone = resolveCanadianProvinceTimeZone(options?.provinceCode ?? null);

  // Iterate: adjust a UTC guess until the civil clock in `timeZone` is 23:59:59 on that day.
  let utcMs = Date.UTC(year, month - 1, day, 23, 59, 59, 999);
  for (let attempt = 0; attempt < 4; attempt += 1) {
    const parts = resolveCivilClockPartsInTimeZone(new Date(utcMs), timeZone);
    const gotAsUtc = Date.UTC(
      Number(parts.year),
      Number(parts.month) - 1,
      Number(parts.day),
      Number(parts.hour),
      Number(parts.minute),
      Number(parts.second),
      999,
    );
    const wantAsUtc = Date.UTC(year, month - 1, day, 23, 59, 59, 999);
    const delta = wantAsUtc - gotAsUtc;
    if (delta === 0) {
      break;
    }
    utcMs += delta;
  }

  return utcMs;
}

/**
 * Reads year/month/day/hour/minute/second for an instant in the Ship From IANA zone,
 * or the runtime local zone when `provinceCode` is omitted (`undefined`).
 * Passing `null` resolves the default Eastern Canada zone (not the browser zone).
 */
export function resolveCivilClockParts(
  date: Date,
  provinceCode?: string | null,
): {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
} {
  const timeZone =
    provinceCode === undefined ? undefined : resolveCanadianProvinceTimeZone(provinceCode);
  return resolveCivilClockPartsInTimeZone(date, timeZone);
}

/**
 * Formats civil clock parts for an instant in an explicit IANA zone (or runtime local when omitted).
 */
function resolveCivilClockPartsInTimeZone(
  date: Date,
  timeZone: string | undefined,
): {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
  second: string;
} {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const values: Record<string, string> = {};
  for (const part of formatter.formatToParts(date)) {
    if (part.type !== 'literal') {
      values[part.type] = part.value;
    }
  }

  return {
    year: values['year'] ?? '0000',
    month: values['month'] ?? '00',
    day: values['day'] ?? '00',
    hour: values['hour'] ?? '00',
    minute: values['minute'] ?? '00',
    second: values['second'] ?? '00',
  };
}
